import { useMemo, CSSProperties } from 'react'
import { Departure, CUM_MIN } from './researchStudioModel'
import type { DayModel } from './ResearchSchedule'
import { exportTimetableXlsx, TtExportWorking, TtExportPattern } from './researchTimetableXlsx'

// Month timetable in thebigplan's TTBuilder format (Studio v5, Chris 2026-07-22).
//
// The Weekly Service Schedule's per-day-of-week modelled timetables are grouped
// into WTT PATTERNS (one banner per genuinely different timetable — Weekday /
// Saturday / Sunday, plus any customised day) and rendered exactly like the
// TTBuilder grid: North then South blocks with Headcode / Loco / Guard / Set
// header rows, the 9 timing-point slots (Pickering · Levisham · Newtondale ·
// Goathland · Grosmont · Whitby) with d/a markers, and a Forms row chaining
// each carriage set into its next working. "Export timetable" downloads the
// stakeholders' WTT workbook via the same ExcelJS builder thebigplan uses.

const TEAL = '#0b3d2e'
const DOW_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const ROWS: Record<'N' | 'S', [string, string][]> = {
  N: [['PIC', 'd'], ['LEV', 'a'], ['LEV', 'd'], ['NEW', 'f'], ['GOA', 'a'], ['GOA', 'd'], ['GRO', 'a'], ['GRO', 'd'], ['WHB', 'a']],
  S: [['WHB', 'd'], ['GRO', 'a'], ['GRO', 'd'], ['GOA', 'a'], ['GOA', 'd'], ['NEW', 'f'], ['LEV', 'a'], ['LEV', 'd'], ['PIC', 'a']],
}
const ST_NAME: Record<string, string> = { PIC: 'Pickering', LEV: 'Levisham', NEW: 'Newtondale (req)', GOA: 'Goathland', GRO: 'Grosmont', WHB: 'Whitby' }
// slot index per (our station index → a/d), per direction
const SLOT: Record<'N' | 'S', Record<number, { a?: number; d?: number }>> = {
  N: { 0: { d: 0 }, 1: { a: 1, d: 2 }, 2: { a: 4, d: 5 }, 3: { a: 6, d: 7 }, 4: { a: 8 } },
  S: { 4: { d: 0 }, 3: { a: 1, d: 2 }, 2: { a: 3, d: 4 }, 1: { a: 6, d: 7 }, 0: { a: 8 } },
}
const N_DEST: Record<number, string> = { 4: 'T', 3: 'G', 2: 'A', 1: 'L' }
const S_DEST: Record<number, string> = { 0: 'P', 1: 'L', 2: 'A', 3: 'G' }

const round5near = (x: number) => Math.round(x / 5) * 5
const ceil5 = (x: number) => Math.ceil(x / 5) * 5
const tok = (mins: number) => `${String(Math.floor(mins / 60)).padStart(2, '0')}${String(Math.round(mins) % 60).padStart(2, '0')}`

/** Derive the 9 timing tokens for a leg — departures as stored, arrivals to nearest 5, intermediate departures rounded up (+2 min dwell).
 *  EXPORTED: the exec PDF's season poster and per-pattern diagram pages derive
 *  station times for the banked season plans through this same rule. */
export function slotTimes(leg: Departure): (string | null)[] {
  const dir: 'N' | 'S' = leg.d > leg.o ? 'N' : 'S'
  const t: (string | null)[] = Array(9).fill(null)
  const step = dir === 'N' ? 1 : -1
  for (let s = leg.o; dir === 'N' ? s <= leg.d : s >= leg.d; s += step) {
    const raw = leg.minutes + Math.abs(CUM_MIN[s] - CUM_MIN[leg.o])
    const slots = SLOT[dir][s]
    if (s === leg.o) { if (slots.d != null) t[slots.d] = tok(leg.minutes) }
    else if (s === leg.d) { if (slots.a != null) t[slots.a] = tok(round5near(raw)) }
    else {
      if (slots.a != null) t[slots.a] = tok(round5near(raw))
      if (slots.d != null) t[slots.d] = tok(ceil5(raw + 2))
    }
  }
  return t
}

export interface TtWork extends TtExportWorking { id: string; capacity: number }
export interface TtPattern { key: string; name: string; dows: number[]; N: TtWork[]; S: TtWork[]; setsUsed: string }

function patternName(dows: number[]): string {
  const k = dows.join(',')
  if (k === '6') return 'Saturday Timetable'
  if (k === '7') return 'Sunday Timetable'
  if (k === '1,2,3,4,5') return 'Weekday Timetable (Mon–Fri)'
  return `${dows.map((d) => DOW_SHORT[d - 1]).join(', ')} Timetable`
}

interface Props {
  models: Record<number, DayModel | null>
  weeks: (number | null)[][]
  monthLabel: string // "July 2026"
  season: string
  viewLabel: string
}

/**
 * Group the per-day-of-week models into WTT PATTERNS (one per genuinely
 * different timetable) with thebigplan-style headcodes, sets, locos and Forms
 * chains. Pure — the exec PDF's season-timetable pages reuse it.
 */
export function buildTtPatterns(models: Record<number, DayModel | null>): TtPattern[] {
    // Group days of week by identical modelled timetable.
    const sig = (m: DayModel) => JSON.stringify(
      m.applied.filter((x) => x.enabled)
        .sort((a, b) => a.minutes - b.minutes)
        .map((x) => [x.direction, x.time, x.o, x.d, x.coaches, m.setBy[x.id] ?? ''])
    )
    const groups = new Map<string, number[]>()
    for (let dow = 1; dow <= 7; dow++) {
      const m = models[dow]
      if (!m) continue
      const s = sig(m)
      groups.set(s, [...(groups.get(s) ?? []), dow])
    }
    return [...groups.values()].map((dows) => {
      const m = models[dows[0]]!
      const legs = m.applied.filter((x) => x.enabled).sort((a, b) => a.minutes - b.minutes)
      const nLegs = legs.filter((l) => l.d > l.o)
      const sLegs = legs.filter((l) => l.d < l.o)
      const headcodes = new Map<string, string>()
      nLegs.forEach((l, i) => headcodes.set(l.id, `1${N_DEST[l.d] ?? 'T'}${String(i * 2 + 1).padStart(2, '0')}`))
      sLegs.forEach((l, i) => headcodes.set(l.id, `1${S_DEST[l.d] ?? 'P'}${String(i * 2 + 2).padStart(2, '0')}`))
      // Forms: each carriage set's legs in time order — a working forms the set's next one.
      const forms = new Map<string, string>()
      const bySet = new Map<string, Departure[]>()
      for (const l of legs) {
        const code = m.setBy[l.id]
        if (!code) continue
        bySet.set(code, [...(bySet.get(code) ?? []), l])
      }
      for (const chain of bySet.values()) {
        chain.sort((a, b) => a.minutes - b.minutes)
        for (let i = 0; i + 1 < chain.length; i++) forms.set(chain[i].id, headcodes.get(chain[i + 1].id) ?? '')
      }
      const mkWork = (l: Departure): TtWork => ({
        id: l.id,
        headcode: headcodes.get(l.id) ?? '',
        loco: m.locoBy[l.id]?.code ?? '',
        guard: '',
        set: m.setBy[l.id] ?? '',
        forms: forms.get(l.id) ?? '',
        t: slotTimes(l),
        capacity: l.capacity ?? (l.coaches === 0 ? 48 : l.coaches * 64),
      })
      return {
        key: dows.join(','),
        name: patternName(dows),
        dows,
        N: nLegs.map(mkWork),
        S: sLegs.map(mkWork),
        setsUsed: [...bySet.keys()].join(' '),
      }
    }).sort((a, b) => a.dows[0] - b.dows[0])
}

export function ResearchTimetable({ models, weeks, monthLabel, season, viewLabel }: Props) {
  const patterns = useMemo((): TtPattern[] => buildTtPatterns(models), [models])

  const monthName = monthLabel.split(' ')[0]
  const datesFor = (dows: number[]): string[] => {
    const days: number[] = []
    for (const row of weeks) row.forEach((d, col) => { if (d != null && dows.includes(col + 1)) days.push(d) })
    days.sort((a, b) => a - b)
    return days.length ? [`${monthName}   ${days.join(' . ')}`] : []
  }

  if (patterns.length === 0) return null

  const doExport = () => {
    const data = {
      title: `${monthLabel} Studio Timetable`.toUpperCase(),
      season,
      patterns: patterns.map((p): TtExportPattern => ({
        name: p.name,
        dates: datesFor(p.dows),
        N: p.N,
        S: p.S,
      })),
    }
    void exportTimetableXlsx(data)
  }

  const cellS: CSSProperties = { border: '1px solid #e5e1d6', padding: '2px 6px', textAlign: 'center', minWidth: 44 }
  const labS: CSSProperties = { border: '1px solid #e5e1d6', padding: '2px 6px', textAlign: 'left', whiteSpace: 'nowrap', color: '#6b6b6b' }

  return (
    <div style={{ marginTop: 14, borderTop: '2px solid #e5e1d6', paddingTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: TEAL }}>Working timetable — {monthLabel} ({viewLabel})</div>
          <div style={{ fontSize: '0.72rem', color: '#8a8a8a', maxWidth: 760 }}>
            thebigplan TTBuilder format: one pattern per distinct timetable; Headcode / Loco / Guard / Set header rows, timing points with
            d/a markers (arrivals to nearest 5 min, intermediate departures rounded up), and a <strong>Forms</strong> row chaining each carriage
            set into its next working. Export opens in the stakeholders&apos; WTT workbook layout.
          </div>
        </div>
        <button
          type="button"
          onClick={doExport}
          style={{ background: TEAL, color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontWeight: 700 }}
        >Export timetable (Excel)</button>
      </div>

      {patterns.map((p) => (
        <div key={p.key} style={{ marginBottom: 14 }}>
          <div style={{ background: TEAL, color: '#fff', fontWeight: 700, fontSize: '0.8rem', padding: '4px 10px', borderRadius: '6px 6px 0 0', textAlign: 'center' }}>
            {p.name.toUpperCase()} — {season}
          </div>
          <div style={{ border: '1px solid #e5e1d6', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '6px 10px', background: '#fff' }}>
            <div style={{ fontSize: '0.72rem', color: '#6b6b6b', marginBottom: 6 }}>
              {datesFor(p.dows).join(' · ')} &nbsp;·&nbsp; sets in traffic: <strong>{p.setsUsed || '—'}</strong>
            </div>
            {(['N', 'S'] as const).map((dir) => {
              const works = p[dir]
              if (works.length === 0) return null
              const rows = ROWS[dir]
              const used = rows.map((_r, i) => works.some((w) => !!w.t[i]))
              return (
                <div key={dir} style={{ overflowX: 'auto', marginBottom: 8 }}>
                  <div style={{ fontSize: '0.74rem', fontWeight: 700, color: TEAL, margin: '4px 0 2px' }}>{dir === 'N' ? 'Northbound' : 'Southbound'}</div>
                  <table style={{ borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                    <tbody>
                      {([['Headcode', (w: TtWork) => w.headcode, true], ['Loco', (w: TtWork) => w.loco, false], ['Set', (w: TtWork) => `${w.set}`, false], ['Seats', (w: TtWork) => String(w.capacity), false]] as const).map(([label, fn, strong]) => (
                        <tr key={label}>
                          <td style={labS} colSpan={2}><strong>{label}</strong></td>
                          {works.map((w) => <td key={w.id} style={{ ...cellS, fontWeight: strong ? 700 : 400 }}>{fn(w)}</td>)}
                        </tr>
                      ))}
                      {rows.map(([st, k], i) => {
                        if (!used[i]) return null
                        const firstOfStation = i === rows.findIndex(([s2], j) => s2 === st && used[j])
                        return (
                          <tr key={`${st}-${k}`}>
                            <td style={labS}>{firstOfStation ? ST_NAME[st] : ''}</td>
                            <td style={{ ...labS, textAlign: 'center', width: 18 }}>{k === 'f' ? '' : k}</td>
                            {works.map((w) => <td key={w.id} style={cellS}>{w.t[i] ?? ''}</td>)}
                          </tr>
                        )
                      })}
                      <tr>
                        <td style={labS} colSpan={2}><strong>Forms</strong></td>
                        {works.map((w) => <td key={w.id} style={cellS}>{w.forms}</td>)}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
